import React, { useState, useEffect, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Stack,
  Alert,
  Slider,
  Button,
  Dialog,
  Tooltip,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CropIcon from "@mui/icons-material/Crop";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  getCroppedImageFile,
  MARKER_ASPECT,
  MARKER_OUTPUT_WIDTH,
} from "../../../utils/CropImage";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export const ImageCropModal = ({
  open,
  file,
  onCancel,
  onConfirm,
  title = "Recortar imagen",
  aspect = MARKER_ASPECT,
  outputWidth = MARKER_OUTPUT_WIDTH,
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !file) {
      setImageSrc(null);
      return;
    }

    const url = URL.createObjectURL(file);

    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setRotation(0);
    setCroppedAreaPixels(null);
    setError("");

    return () => URL.revokeObjectURL(url);
  }, [open, file]);

  const handleCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setRotation(0);
  };

  const handleRotate = (degrees) => {
    setRotation((prev) => (prev + degrees + 360) % 360);
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      setError("Ajusta el encuadre antes de continuar.");
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const croppedFile = await getCroppedImageFile(
        imageSrc,
        croppedAreaPixels,
        rotation,
        {
          name: file?.name,
          type: file?.type,
          maxWidth: outputWidth,
          aspect,
        }
      );

      onConfirm(croppedFile);
    } catch (err) {
      setError(err.message || "No se pudo recortar la imagen.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CropIcon color="primary" />
          <span>{title}</span>
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block">
          Arrastra para encuadrar y usa el zoom. La imagen se guardará en
          formato 16:9 (máx. {outputWidth}×{Math.round(outputWidth / aspect)} px).
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 280, sm: 380 },
            backgroundColor: "#0f172a",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              cropShape="rect"
              showGrid
              restrictPosition
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              zoomSpeed={0.2}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={handleCropComplete}
            />
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mt: 3, px: 1 }}
        >
          <ZoomOutIcon color="action" />

          <Slider
            value={zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.05}
            onChange={(_e, value) => setZoom(value)}
            disabled={processing}
            aria-label="Zoom"
          />

          <ZoomInIcon color="action" />
        </Stack>

        <Stack
          direction="row"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Tooltip title="Girar 90° a la izquierda">
            <span>
              <IconButton
                onClick={() => handleRotate(-90)}
                disabled={processing}
              >
                <RotateLeftIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Girar 90° a la derecha">
            <span>
              <IconButton
                onClick={() => handleRotate(90)}
                disabled={processing}
              >
                <RotateRightIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Restablecer encuadre">
            <span>
              <IconButton onClick={handleReset} disabled={processing}>
                <RestartAltIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel} disabled={processing} color="inherit">
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={processing || !croppedAreaPixels}
          startIcon={
            processing ? <CircularProgress size={18} /> : <CropIcon />
          }
        >
          {processing ? "Recortando..." : "Aplicar recorte"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
